import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Cluster, MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer'
import { IContactsGeoLocationsItem } from '@lla-platform/core/core-data-access'
import { IShopLocationItem, ShopsState } from '@lla-platform/shops/shops-data-access'
import { Store } from '@ngxs/store'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { ISelectItem } from '@lla-platform/core/core-ui'
import { Options } from '@angular-slider/ngx-slider'
import { debounceTime } from 'rxjs'

@UntilDestroy()
@Component({
  selector: 'lla-contact-map',
  templateUrl: './contact-map.component.html',
  styleUrls: ['./contact-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContactMapComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() enableShopMarkers = true
  @Input() isLoading = false
  @Input() contactsGeoLocations: IContactsGeoLocationsItem[] = []
  @Input() onlyShopMarkers = false
  @Input() allShopsWithCoordinates: IShopLocationItem[] = inject(Store)
    .selectSnapshot(ShopsState.allShops)
    ?.filter((el) => el.latitude && el.longitude)

  @Output() closed = new EventEmitter<void>()

  @ViewChild('map', { static: false }) mapElement: ElementRef

  form: UntypedFormGroup
  allShopsDropDown: ISelectItem[] = []
  sliderOptions: Options = {
    floor: 1,
    ceil: 50
  }

  map: google.maps.Map
  markerCluster: MarkerClusterer
  mapOptions: google.maps.MapOptions = {
    center: { lat: 40, lng: -100 },
    zoom: 5,
    streetViewControl: false,
    cameraControl: false,
    zoomControl: true,
    fullscreenControlOptions: {
      position: google.maps.ControlPosition.BOTTOM_RIGHT
    },
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.business',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.attraction',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.government',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.medical',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.park',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.place_of_worship',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.school',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.sports_complex',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'transit',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      }
    ]
  }
  markers: google.maps.Marker[] = []
  shopsMarkers: google.maps.Marker[] = []
  overlay: google.maps.OverlayView | null = null
  private infoWindow: google.maps.InfoWindow
  private radiusCircle: google.maps.Circle | null = null
  private currentShopInfo: IShopLocationItem | null = null

  constructor(
    private fb: UntypedFormBuilder,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      shopId: [null],
      shopLabel: [null],
      radius: [1]
    })

    this.allShopsDropDown = this.allShopsWithCoordinates.map((el) => ({
      label: el.name ?? el.nickname ?? '',
      value: el.id
    }))

    this.form
      .get('shopId')
      ?.valueChanges.pipe(untilDestroyed(this), debounceTime(300))
      .subscribe((shopId) => {
        this.setShopLabel()
        if (shopId) {
          this.selectShopMarker(shopId)
        } else {
          // If shopId is cleared, close any open info window and clear radius circle
          this.infoWindow.close()
          this.clearRadiusCircle()
          this.currentShopInfo = null
        }
      })

    this.form
      .get('radius')
      ?.valueChanges.pipe(untilDestroyed(this), debounceTime(300))
      .subscribe(() => {
        if (this.radiusCircle && this.currentShopInfo) {
          // Redraw the circle with new radius
          this.drawRadiusCircle(this.currentShopInfo)
          // Update the info window content to reflect the new radius
          this.setInfoWindowContent(undefined, this.currentShopInfo)
        }
      })
  }

  ngOnChanges(changes: SimpleChanges): void {
    const contactsGeoLocations: IContactsGeoLocationsItem[] =
      changes['contactsGeoLocations']?.currentValue

    if (!this.onlyShopMarkers && contactsGeoLocations) {
      this.setMarkersInfo()
    }
  }

  ngAfterViewInit(): void {
    this.map = new google.maps.Map(this.mapElement.nativeElement, this.mapOptions)

    if (this.onlyShopMarkers) {
      this.setShopsMarkers()
    }

    // Create a single InfoWindow that will be reused for all markers
    this.infoWindow = new google.maps.InfoWindow({
      content: '',
      disableAutoPan: true,
      headerDisabled: true
    })

    // InfoWindow styles and event listeners
    this.infoWindow.addListener('domready', () => {
      const containerParent = document.querySelector('.gm-style-iw') as HTMLElement
      if (containerParent) {
        containerParent.style.padding = '0'
      }

      const containerChild = document.querySelector('.gm-style-iw-d') as HTMLElement
      if (containerChild) {
        containerChild.style.overflow = 'auto'
        containerChild.style.maxHeight = 'auto'
      }

      // Add event listeners after DOM is ready
      this.attachInfoWindowEventListeners()
    })

    // Close InfoWindow and radius circle when clicking on the map
    this.map.addListener('click', () => {
      this.infoWindow.close()
      this.clearRadiusCircle()
      this.clearDropdownSelection()
    })

    // Reset currentShopInfo when InfoWindow is closed
    this.infoWindow.addListener('closeclick', () => {
      this.clearRadiusCircle()
      this.clearDropdownSelection()
    })

    // Fix slider width issue when component becomes visible
    this.refreshSliderLayout()
  }

  setMarkersInfo() {
    this.contactsGeoLocations = this.contactsGeoLocations?.filter((el) => el.latitude && el.longitude)
    this.setContactsMarkers()
    if (this.enableShopMarkers) {
      this.setShopsMarkers()
    }
  }

  setContactsMarkers() {
    let markerSize = new google.maps.Size(24, 30)

    if (this.contactsGeoLocations && this.contactsGeoLocations.length === 1) {
      this.map.setCenter({
        lat: this.contactsGeoLocations[0].latitude,
        lng: this.contactsGeoLocations[0].longitude
      })
      this.map.setZoom(10)
      markerSize = new google.maps.Size(30, 35)
    }

    this.markers = this.contactsGeoLocations.map((el) => {
      const marker = new google.maps.Marker({
        draggable: false,
        position: { lat: el.latitude, lng: el.longitude },
        map: this.map,
        icon: {
          url: 'assets/images/icons/new-map-marker2.svg',
          scaledSize: markerSize
        },
        zIndex: 1000
      })

      marker.addListener('mouseover', () => {
        marker.setIcon({
          url: 'assets/images/icons/new-map-marker-hover2.svg',
          scaledSize: markerSize
        })
      })

      marker.addListener('mouseout', () => {
        marker.setIcon({
          url: 'assets/images/icons/new-map-marker2.svg',
          scaledSize: markerSize
        })
      })

      // Add click event to open InfoWindow with smart positioning
      marker.addListener('click', () => {
        this.clearRadiusCircle()
        this.clearDropdownSelection()
        this.openInfoWindowWithSmartPositioning(marker, el)
      })

      return marker
    })
    this.markerCluster = new MarkerClusterer({
      map: this.map,
      markers: this.markers,
      renderer: {
        render: this.renderContactsClusterMarker
      },
      algorithm: new SuperClusterAlgorithm({})
    })
  }

  setShopsMarkers() {
    const markerSize = new google.maps.Size(30, 38)

    this.shopsMarkers = this.allShopsWithCoordinates.map((el) => {
      const marker = new google.maps.Marker({
        draggable: false,
        position: { lat: el.latitude, lng: el.longitude },
        map: this.map,
        icon: {
          url: 'assets/images/icons/shop_marker2.svg',
          scaledSize: markerSize
        },
        zIndex: 10000
      })

      // Add click event to open InfoWindow with smart positioning and draw radius circle
      marker.addListener('click', () => {
        this.drawRadiusCircle(el)
        this.openInfoWindowWithSmartPositioning(marker, undefined, el)
        // Update the dropdown to select this shop
        this.form.get('shopId')?.setValue(el.id, { emitEvent: false })
        this.setShopLabel()
      })

      marker.addListener('mouseover', () => {
        marker.setIcon({
          url: 'assets/images/icons/shop_marker-hover2.svg',
          scaledSize: markerSize
        })
      })

      marker.addListener('mouseout', () => {
        marker.setIcon({
          url: 'assets/images/icons/shop_marker2.svg',
          scaledSize: markerSize
        })
      })

      return marker
    })
  }

  selectShopMarker(shopId: string) {
    // Find the shop info
    const shopInfo = this.allShopsWithCoordinates.find((shop) => shop.id === shopId)
    if (!shopInfo) {
      return
    }

    // Find the corresponding marker
    const shopIndex = this.allShopsWithCoordinates.findIndex((shop) => shop.id === shopId)
    if (shopIndex === -1 || !this.shopsMarkers[shopIndex]) {
      return
    }

    const marker = this.shopsMarkers[shopIndex]

    // Trigger the marker click programmatically
    google.maps.event.trigger(marker, 'click')

    const position = marker.getPosition()
    if (position) {
      this.map.panTo(position)
      const zoom = this.map.getZoom()
      if (zoom && zoom < 12) {
        this.map.setZoom(12)
      }
    }
  }

  renderContactsClusterMarker({ position, count }: Cluster) {
    return new google.maps.Marker({
      position,
      icon: {
        url: 'assets/images/icons/cluster-marker2.svg',
        scaledSize: new google.maps.Size(45, 45)
      },
      label: {
        text: String(count),
        color: 'black',
        fontSize: '12px'
      },
      title: `Cluster of ${count} markers`,
      // adjust zIndex to be above other markers
      zIndex: 1500 + count
    })
  }

  private openInfoWindowWithSmartPositioning(
    marker: google.maps.Marker,
    contactInfo?: IContactsGeoLocationsItem,
    shopInfo?: IShopLocationItem
  ): void {
    // Close any existing info window
    this.infoWindow.close()

    // Set info window content
    this.setInfoWindowContent(contactInfo, shopInfo)

    // Get marker position in screen coordinates
    const projection = this.map.getProjection()
    const markerPosition = marker.getPosition()

    if (!projection || !markerPosition) {
      // Fallback to default behavior if projection is not available
      this.infoWindow.open(this.map, marker)
      return
    }

    // Get map bounds and container dimensions
    const mapBounds = this.map.getBounds()
    const mapDiv = this.map.getDiv()
    const mapRect = mapDiv.getBoundingClientRect()

    if (!mapBounds) {
      // Fallback to default behavior if bounds are not available
      this.infoWindow.open(this.map, marker)
      return
    }

    // Calculate marker position relative to viewport
    const markerLatLng = markerPosition
    const markerPixel = projection.fromLatLngToPoint(markerLatLng)
    const mapCenter = this.map.getCenter()
    const zoom = this.map.getZoom()

    if (!markerPixel || !mapCenter || zoom === undefined) {
      // Fallback to default behavior if calculations fail
      this.infoWindow.open(this.map, marker)
      return
    }

    const mapCenterPixel = projection.fromLatLngToPoint(mapCenter)

    if (!mapCenterPixel) {
      // Fallback to default behavior if calculations fail
      this.infoWindow.open(this.map, marker)
      return
    }

    const scale = Math.pow(2, zoom)

    // Convert to screen coordinates
    const markerScreenY = (markerPixel.y - mapCenterPixel.y) * scale + mapRect.height / 2

    const isNearTopEdge = markerScreenY < 150 // Within 150px of top edge

    // Configure InfoWindow options based on position
    const infoWindowOptions: google.maps.InfoWindowOptions = {
      content: this.infoWindow.getContent(),
      disableAutoPan: false, // Enable auto-pan for better positioning
      headerDisabled: true
    }

    // If marker is near the top edge, we want the info window to appear below
    // If marker is near the bottom edge, we want it to appear above
    if (isNearTopEdge) {
      // Force the info window to appear below the marker
      infoWindowOptions.pixelOffset = new google.maps.Size(0, 10)
    } else {
      // Use default positioning for markers in the middle area
      infoWindowOptions.pixelOffset = new google.maps.Size(0, 0)
    }

    // Create a new info window with the calculated options
    this.infoWindow = new google.maps.InfoWindow(infoWindowOptions)

    // Re-apply styles and add event listeners when InfoWindow is ready
    this.infoWindow.addListener('domready', () => {
      const containerParent = document.querySelector('.gm-style-iw') as HTMLElement
      if (containerParent) {
        containerParent.style.padding = '0'
      }

      const containerChild = document.querySelector('.gm-style-iw-d') as HTMLElement
      if (containerChild) {
        containerChild.style.overflow = 'auto'
        containerChild.style.maxHeight = 'auto'
      }

      // Add event listeners after DOM is ready
      this.attachInfoWindowEventListeners()
    })

    // Open the info window
    this.infoWindow.open(this.map, marker)
  }

  ngOnDestroy(): void {
    if (this.infoWindow) {
      this.infoWindow.close()
    }
    this.clearRadiusCircle()
  }

  setInfoWindowContent(contactInfo?: IContactsGeoLocationsItem, shopInfo?: IShopLocationItem) {
    // Store current shop info for event listeners
    this.currentShopInfo = shopInfo || null

    if (contactInfo) {
      this.infoWindow.setContent(`
        <div class="p-base w-[200px] md:w-[300px]">
          <div class="flex justify-between gap-sm">
            <div class="flex-1 text-base leading-base font-semibold text-black">${contactInfo.name}</div>
            <div id="close-info-window-btn" class="text-sm leading-sm text-black cursor-pointer">✕</div>
          </div>
          <div class="mt-lg text-sm leading-sm text-black flex flex-col gap-xs">
            <span>${contactInfo.address}</span>
            <span>${contactInfo.city}, ${contactInfo.state}, ${contactInfo.country}</span>
          </div>

          <div class="mt-xl">
            <a href="${contactInfo.url}" target="_blank" class="text-sm leading-sm text-primary decoration-none !outline-none">View Contact Details 
              <span class="text-lg">→</span>
            </a>
          </div>
        </div>
      `)
    } else if (shopInfo) {
      const contactsInRadius = this.getContactsWithinRadius(shopInfo)?.length
      const allContacts = this.contactsGeoLocations?.length
      this.infoWindow.setContent(`
        <div class="p-base w-[200px] md:w-[300px]">
          <div class="flex justify-between gap-sm">
            <div class="flex-1 text-base leading-base font-semibold text-black">${shopInfo.name}</div>
            <div id="close-info-window-btn" class="text-sm leading-sm text-black cursor-pointer">✕</div>
          </div>
          <div class="mt-lg text-sm leading-sm text-black flex flex-col gap-xs">
             ${shopInfo.address}
          </div>

          ${
            this.onlyShopMarkers
              ? ''
              : `
            <div class="mt-lg text-sm leading-sm text-black flex flex-col gap-xs">
              ${contactsInRadius} out of ${allContacts} contacts (${((contactsInRadius / allContacts) * 100).toFixed(0)}%) are located within a ${this.form.get('radius')?.value}-mile radius of the shop.
            </div>
          `
          }
        </div>
      `)
    }
  }

  private attachInfoWindowEventListeners() {
    // Always add close button event listener
    const closeInfoWindowBtn = document.getElementById('close-info-window-btn')
    if (closeInfoWindowBtn) {
      closeInfoWindowBtn.addEventListener('click', () => {
        this.infoWindow.close()
        this.clearRadiusCircle()
        this.clearDropdownSelection()
      })
    }
  }

  drawRadiusCircle(shopInfo: IShopLocationItem) {
    if (this.onlyShopMarkers) {
      return
    }

    // Clear any existing circle
    this.clearRadiusCircle()

    // Create a blue circle with 50% alpha background
    this.radiusCircle = new google.maps.Circle({
      strokeColor: '#0066CC',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#0066CC',
      fillOpacity: 0.5,
      map: this.map,
      center: { lat: shopInfo.latitude, lng: shopInfo.longitude },
      radius: (this.form.get('radius')?.value ?? 0) * 1609.34 // Convert mile to meters
    })

    // Add click event listener to the circle to close InfoWindow and clear circle
    this.radiusCircle.addListener('click', () => {
      this.infoWindow.close()
      this.clearRadiusCircle()
      this.clearDropdownSelection()
    })
  }

  clearRadiusCircle() {
    if (this.radiusCircle) {
      this.radiusCircle.setMap(null)
      this.radiusCircle = null
    }
  }

  private getContactsWithinRadius(shopInfo: IShopLocationItem): IContactsGeoLocationsItem[] {
    const shopLat = shopInfo.latitude
    const shopLng = shopInfo.longitude
    const radiusInMeters = (this.form.get('radius')?.value ?? 0) * 1609.34 // Convert mile to meters

    return this.contactsGeoLocations.filter((contact) => {
      const distance = this.calculateDistance(shopLat, shopLng, contact.latitude, contact.longitude)
      return distance <= radiusInMeters
    })
  }

  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    // Using Haversine formula to calculate distance between two points
    const R = 6371000 // Earth's radius in meters
    const dLat = this.toRadians(lat2 - lat1)
    const dLng = this.toRadians(lng2 - lng1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c // Distance in meters
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180)
  }

  /**
   * Refreshes the slider layout to fix width calculation issues when component becomes visible
   */
  private refreshSliderLayout(): void {
    // Use setTimeout to ensure the modal is fully rendered before triggering the resize
    setTimeout(() => {
      // Trigger a window resize event to force the slider to recalculate its dimensions
      window.dispatchEvent(new Event('resize'))

      // Force change detection to ensure any pending updates are applied
      this.cdr.detectChanges()
    }, 100)

    // Add an additional timeout for more stubborn cases
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 300)
  }

  clearDropdownSelection() {
    this.form.get('shopId')?.setValue(null, { emitEvent: false })
    this.currentShopInfo = null
    this.setShopLabel()
  }

  setShopLabel() {
    this.form
      .get('shopLabel')
      ?.setValue(this.allShopsDropDown.find((el) => el.value === this.form.get('shopId')?.value)?.label)
  }
}
